import { HTMLAttributes, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Button, Dialog, DropdownMenu } from '../../ui';
import { PencilIcon, TrashIcon } from '../../icons';
import { InlineSpinner } from '../spinner';
import { CardIcon } from './card-icon';
import { usePaymentMethod } from '../../../hooks/api/useStripe';
import useAuth from '../../../hooks/utils/useAuth';
import { ConfirmRemovePaymentMethodModal } from './confirm-remove-payment-method-modal';

export interface PaymentMethodProps extends HTMLAttributes<HTMLDivElement> {
  onUpdateButtonClick?: () => void;
  onSuccessRemovePayment?: () => void;
  useDrawerModal?: boolean;
}

export const PaymentMethod: React.FC<PaymentMethodProps> = ({
  onUpdateButtonClick,
  onSuccessRemovePayment,
  useDrawerModal = false,
  className,
  ...props
}) => {
  const { user } = useAuth();
  const [showRemoveCardModal, setShowRemoveCardModal] = useState<boolean>(false);

  const {
    data: { payment_method } = {},
    isLoading: isPaymentMethodLoading,
    removePaymentMethod,
  } = usePaymentMethod(user?.id);

  const menuItems = [
    ...(onUpdateButtonClick
      ? [
          {
            onClick: onUpdateButtonClick,
            title: 'Update',
            icon: <PencilIcon />,
          },
        ]
      : []),
    {
      onClick: () => setShowRemoveCardModal(true),
      title: 'Remove',
      icon: <TrashIcon />,
    },
  ];

  const deletePaymentMethod = async () => {
    if (user?.id) {
      await removePaymentMethod.mutateAsync(user?.id);

      setShowRemoveCardModal(false);

      if (onSuccessRemovePayment) {
        onSuccessRemovePayment();
      }
    }
  };

  if (isPaymentMethodLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <InlineSpinner />
      </div>
    );
  }

  return (
    <>
      {payment_method?.card && (
        <div className={twMerge('flex items-center justify-between', className)} {...props}>
          <div className="flex items-center space-x-4">
            <CardIcon brandName={payment_method.card.brand} className="h-12 w-12 md:h-8" />
            <div className="text-dark text-base font-medium leading-normal">*{payment_method.card.last4}</div>
          </div>
          <DropdownMenu items={menuItems} />
        </div>
      )}
      {useDrawerModal ? (
        <ConfirmRemovePaymentMethodModal
          open={!!showRemoveCardModal}
          onConfirm={deletePaymentMethod}
          onCancel={() => setShowRemoveCardModal(false)}
          isLoading={removePaymentMethod.isLoading}
        />
      ) : (
        <Dialog
          open={!!showRemoveCardModal}
          onClose={() => setShowRemoveCardModal(false)}
          closeOnOutsideClick={!removePaymentMethod.isLoading}
          className="backdrop:bg-modalBackdropColor inset-y-1/2 -translate-y-1/2 rounded-2xl"
        >
          <div className="h-30 inline-flex w-80 flex-col items-center justify-center gap-4 p-8">
            <div className="flex flex-col">
              <div className="font-manrope self-stretch text-center text-xl font-bold leading-loose text-gray-800">
                Are you sure?
              </div>
              <div className="font-manrope text-center text-stone-500">
                Are you sure you want to remove your payment method? You won't be able to have additional call minutes
                without adding it again
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-4">
            <Button
              onClick={() => setShowRemoveCardModal(false)}
              variant="secondary"
              disabled={removePaymentMethod.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => deletePaymentMethod()}
              disabled={removePaymentMethod.isLoading}
              loading={removePaymentMethod.isLoading}
            >
              Remove payment method
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
};
